import React from 'react'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
} from '@/components/ui/sidebar'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { GameRoomDescriptionDisplay } from '@/components/scenario/GameRoomDescription'
import { formatDate } from '@/utils/dateTimeUtils'
import { GameroomEvent } from '@/redux/types/room'
import { ImageIcon } from 'lucide-react'
import { TimerMode } from '@/components/settings/timer-configuration/types'
import { getPhaseNumberLabel } from '@/utils/groupEventsByRound'

export interface EventCollapsibleRowProps {
  event: GameroomEvent
  isOpen: boolean
  onToggle: (isOpen: boolean) => void
  allEvents: GameroomEvent[]
  eventIndex: number
  showPhaseLabel?: boolean
  gameMode: TimerMode | string
}

export const EventCollapsibleRow: React.FC<EventCollapsibleRowProps> = ({
  event,
  isOpen,
  onToggle,
  allEvents,
  eventIndex,
  showPhaseLabel = true,
  gameMode,
}) => {
  const phaseLabel = showPhaseLabel
    ? getPhaseNumberLabel(
        event.released_phase_type,
        gameMode,
        event.released_round_number
      )
    : null

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={onToggle}
      className='border-gray-200 group rounded-md hover:bg-slate-100'
    >
      <SidebarGroup>
        <SidebarGroupLabel className='p-0 h-full'>
          <CollapsibleTrigger className='flex items-start gap-2 w-full transition-all duration-300 px-2 py-1'>
            <img
              src='/icons/chevron-right.svg'
              alt=''
              className='w-3 mt-1 flex-shrink-0 transition-transform group-data-[state=open]:rotate-90'
            />
            <div className='flex flex-col h-full flex-1 min-w-0'>
              <span className='text-xs text-left text-gray-400 flex flex-wrap items-center gap-1'>
                {formatDate(event.event_datetime)}
                {phaseLabel && (
                  <span className='inline-flex items-center rounded-full bg-sky-100 text-sky-800 text-[10px] font-medium px-1.5 py-0.5'>
                    {phaseLabel}
                  </span>
                )}
              </span>
              <span className='text-sm font-semibold text-dark-blue text-start flex items-center gap-1.5'>
                {event.title}
                {event.media && event.media.length > 0 && (
                  <span
                    title='This event has media'
                    className='inline-flex'
                  >
                    <ImageIcon
                      className='w-4 h-4 flex-shrink-0 text-muted-foreground'
                      aria-label='Has media'
                    />
                  </span>
                )}
              </span>
            </div>
          </CollapsibleTrigger>
        </SidebarGroupLabel>
        <CollapsibleContent className='pl-7 pr-2 pb-2 text-sm leading-relaxed text-black CollapsibleContent'>
          <GameRoomDescriptionDisplay
            text={event.text}
            events={allEvents}
            currentEventIndex={eventIndex}
          />
          <SidebarGroupContent />
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  )
}
